import { useEffect, useState, useCallback, ChangeEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  OutlinedInput,
  Pagination,
  Stack,
} from '@mui/material';
import { toast } from 'react-toastify';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { readCoinTypes, updateCoinType, deleteCoinType } from '@/api/stake-db';
import { CoinType, ReadCoinTypesResponse, UpdateCoinTypeRequest } from '@/types/stake-db';
import { Tables } from '@/component/pools/table/Tables';
import { CoinTypeTableColumns } from '../table/StakeDbColumnsData';

interface CoinTypeListProps {
  refreshKey?: number;
}

export default function CoinTypeList({ refreshKey }: CoinTypeListProps) {
  const [list, setList] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const limit = 10;

  // 수정 다이얼로그
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CoinType | null>(null);
  const [editForm, setEditForm] = useState({ symbol: '', contractAddr: '', network: '' });

  // 삭제 다이얼로그
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CoinType | null>(null);

  const fetchCoinTypes = useCallback(async () => {
    setLoading(true);
    try {
      const re = await readCoinTypes({ page, limit });
      if (re.status !== 200) return;

      const response: ReadCoinTypesResponse = re.data;
      const coinTypes = response.coinTypes.map((item: CoinType) => ({
        ...item,
        id: item.coinTypeId,
      }));
      setList(coinTypes);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch coin types:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchCoinTypes();
  }, [fetchCoinTypes, refreshKey]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // 수정
  const handleEditClick = (row: CoinType, event: React.MouseEvent) => {
    event.stopPropagation();
    setEditTarget(row);
    setEditForm({
      symbol: row.symbol || '',
      contractAddr: row.contractAddr || '',
      network: row.network || '',
    });
    setEditOpen(true);
  };

  const handleEditChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setEditForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEditSubmit = async () => {
    if (!editTarget) return;

    try {
      const data: UpdateCoinTypeRequest = {
        coinTypeId: editTarget.coinTypeId,
        symbol: editForm.symbol || undefined,
        contractAddr: editForm.contractAddr || undefined,
        network: editForm.network || undefined,
      };

      const re = await updateCoinType(data);
      if (re.status === 200) {
        toast.success(re.data.message || '코인타입 수정 성공');
        setEditOpen(false);
        fetchCoinTypes();
      } else {
        toast.error(re.data?.message || '코인타입 수정 실패');
      }
    } catch (error) {
      console.error('Update coin type error:', error);
      toast.error('코인타입 수정 실패');
    }
  };

  // 삭제
  const handleDeleteClick = (row: CoinType, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      const re = await deleteCoinType(deleteTarget.coinTypeId);
      if (re.status === 200) {
        toast.success(re.data.message || '코인타입 삭제 성공');
        setDeleteOpen(false);
        fetchCoinTypes();
      } else {
        toast.error(re.data?.message || '코인타입 삭제 실패');
      }
    } catch (error) {
      console.error('Delete coin type error:', error);
      toast.error('코인타입 삭제 실패');
    }
  };

  const columnsWithActions: GridColDef[] = [
    ...CoinTypeTableColumns,
    {
      minWidth: 180,
      field: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => handleEditClick(params.row, e)}
          >
            수정
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={(e) => handleDeleteClick(params.row, e)}
          >
            삭제
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
        {!loading && (
          <Tables
            list={list}
            columns={columnsWithActions}
            getRowId={(row) => row.coinTypeId}
          />
        )}
      </Box>

      <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Stack>

      {/* 수정 다이얼로그 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>코인타입 수정</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Symbol</InputLabel>
              <OutlinedInput
                value={editForm.symbol}
                label="Symbol"
                onChange={handleEditChange('symbol')}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Contract Address</InputLabel>
              <OutlinedInput
                value={editForm.contractAddr}
                label="Contract Address"
                onChange={handleEditChange('contractAddr')}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Network</InputLabel>
              <OutlinedInput
                value={editForm.network}
                label="Network"
                onChange={handleEditChange('network')}
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleEditSubmit}>수정</Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>코인타입 삭제 확인</DialogTitle>
        <DialogContent>
          <b>{deleteTarget?.symbol}</b> ({deleteTarget?.network}) 코인타입을 삭제하시겠습니까?
          <br />
          이 작업은 되돌릴 수 없습니다.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>취소</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>삭제</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
